import { nanoid } from 'nanoid';
import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { Scanner } from '@/models/scanner.model';
import { apiClient } from '@/lib/api/client';
import { CouponsWithImageUrl, CouponStates } from '@/lib/api/coupons.api';
import scanners from '@/utils/scanners';

export type GenerateBatchIdData =
  | { driverId: number; vehicleId: number }
  | { odooDriverId: number; fleetVehicleId: number };

/** Optional paging/search — used by app-membre scanner; omit for physical scanners. */
export type FetchCouponsQuery = {
  searchQuery?: string;
  page?: number;
  pageSize?: number;
};

export const useScannerCouponStore = defineStore('scannerCoupon', {
  state: () => ({
    coupons: reactive<CouponsWithImageUrl[]>([]),
    currentScannerId: null as string | null,
    erroredCoupons: reactive<CouponsWithImageUrl[]>([]),
    /** Total matching `new` coupons from last paged fetch (app-membre). */
    listTotal: 0,
    listPageSize: 25,
    loading: false,
    scanners,
  }),

  getters: {
    currentScanner: (state): Scanner | null => state.scanners[state.currentScannerId] ?? null,
    count(): number {
      if (this.currentScanner === null) {
        return 0;
      }

      return this.currentScanner.count_new_status + this.currentScanner.count_error_status;
    },
    listTotalPages(): number {
      if (this.listPageSize <= 0) return 0;
      return Math.ceil(this.listTotal / this.listPageSize);
    },
  },

  actions: {
    async refetchByCouponId(couponId: number): Promise<void> {
      try {
        this.loading = true;

        const { data, response } = await apiClient.GET('/coupons/{id}', {
          params: {
            path: {
              id: couponId,
            },
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch coupon');
        }

        const index = this.coupons.findIndex((coupon) => coupon.id === couponId);
        if (index !== undefined && index !== -1) {
          this.coupons[index] = data;
        }

        const indexErrored = this.erroredCoupons.findIndex((coupon) => coupon.id === couponId);
        if (indexErrored !== undefined && indexErrored !== -1) {
          this.erroredCoupons[indexErrored] = data;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchBatchCoupons(source: string, batchId: string): Promise<void> {
      try {
        this.loading = true;

        const { data, response } = await apiClient.GET('/coupons', {
          params: {
            query: {
              batchId,
              source,
              state: [
                'approved',
                'archived',
                'cancelled',
                'new',
                'odoo-import-error',
                'odoo-imported',
                'rejected',
              ],
              sortOrder: 'asc',
            },
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch coupons');
        }

        this.coupons = data.coupons;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchNewCoupons(
      source: string,
      couponTypeIds: number[] | null = null,
      query: FetchCouponsQuery = {},
    ): Promise<void> {
      try {
        this.loading = true;

        const queryParams: {
          source: string;
          state: CouponStates[];
          couponTypeId?: number[];
          sortOrder: 'asc' | 'desc';
          searchQuery?: string;
          page?: number;
          pageSize?: number;
        } = {
          source: source,
          state: ['new'],
          sortOrder: 'asc',
        };

        if (couponTypeIds && couponTypeIds.length > 0) {
          queryParams.couponTypeId = couponTypeIds;
        }
        if (query.searchQuery) {
          queryParams.searchQuery = query.searchQuery;
        }
        if (query.page != null) {
          queryParams.page = query.page;
        }
        if (query.pageSize != null) {
          queryParams.pageSize = query.pageSize;
        }

        const { data, response } = await apiClient.GET('/coupons', {
          params: {
            query: queryParams,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch coupons');
        }

        this.coupons = data.coupons;
        if (query.page != null) {
          this.listTotal = data.total ?? 0;
          this.listPageSize = data.pageSize ?? query.pageSize ?? 25;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchErroredCoupons(
      source: string,
      couponTypeIds: number[] | null = null,
      query: FetchCouponsQuery = {},
    ): Promise<void> {
      try {
        this.loading = true;

        const queryParams: {
          importState: (
            | 'odoo-import-error'
            | 'pending-import'
            | 'import-in-odoo'
          )[];
          source: string;
          state: CouponStates[];
          couponTypeId?: number[];
          sortOrder: 'asc' | 'desc';
          searchQuery?: string;
          pageSize?: number;
        } = {
          importState: ['odoo-import-error', 'pending-import', 'import-in-odoo'],
          source,
          state: ['approved'],
          sortOrder: 'asc',
        };

        if (couponTypeIds && couponTypeIds.length > 0) {
          queryParams.couponTypeId = couponTypeIds;
        }
        if (query.searchQuery) {
          queryParams.searchQuery = query.searchQuery;
        }
        // Errored queue is usually small; load a full page without paging chrome.
        if (query.pageSize != null) {
          queryParams.pageSize = query.pageSize;
        } else if (query.searchQuery) {
          queryParams.pageSize = 50;
        }

        const { data, response } = await apiClient.GET('/coupons', {
          params: {
            query: queryParams,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch coupons');
        }

        this.erroredCoupons = data.coupons;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async searchNewCoupons(): Promise<void> {
      try {
        this.loading = true;

        const responses = await Promise.all(
          Object.values(this.scanners).map(async (scanner: Scanner) => {
            const { data, response } = await apiClient.GET('/coupons', {
              params: {
                query: {
                  source: scanner.id,
                  state: ['new'],
                },
              },
            });

            if (!response.ok) {
              throw new Error('Failed to fetch coupons');
            }

            return { ...data, source: scanner.id };
          }),
        );

        for (const response of responses) {
          this.scanners[response.source].count_new_status = response.total;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async searchErroredCoupons(): Promise<void> {
      try {
        this.loading = true;

        const responses = await Promise.all(
          Object.values(this.scanners).map(async (scanner: Scanner) => {
            const { data, response } = await apiClient.GET('/coupons', {
              params: {
                query: {
                  state: ['approved'],
                  importState: ['odoo-import-error', 'pending-import', 'import-in-odoo'],
                  source: scanner.id,
                },
              },
            });

            if (!response.ok) {
              throw new Error('Failed to fetch coupons');
            }

            return { ...data, source: scanner.id };
          }),
        );

        for (const response of responses) {
          this.scanners[response.source].count_error_status = response.total;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    /** Recompute per-scanner totals used by the scanner header `count` (replaces master Firestore listener updates). */
    async refreshAllScannerCounts(): Promise<void> {
      await Promise.all([this.searchNewCoupons(), this.searchErroredCoupons()]);
    },

    async generateBatchId(
      data: GenerateBatchIdData,
      coupons: CouponsWithImageUrl[],
    ): Promise<string | null> {
      if (this.currentScanner.isAppMembre) {
        return;
      }

      try {
        this.loading = true;
        const batchId = nanoid(10);

        await Promise.all(
          coupons.map(async (coupon) => {
            await apiClient.PATCH('/coupons/{id}', {
              params: {
                path: {
                  id: coupon.id,
                },
              },
              // Wipe both id pairs first so re-applying a different shape
               // (e.g. switching from a PG-resolved driver to an Odoo-only driver
               // or vice versa) doesn't leave stale fields on the coupon row,
               // which would let the backend's fallback chains import to the wrong
               // driver/vehicle. The new pair from `data` overrides the nulls.
              body: {
                batchId,
                driverId: null,
                vehicleId: null,
                odooDriverId: null,
                fleetVehicleId: null,
                ...data,
              } as never,
            });
          }),
        );

        return batchId;
      } catch (error) {
        console.error(error);
        return null;
      } finally {
        this.loading = false;
      }
    },

    setCurrentScanner(scannerId: string): void {
      this.currentScannerId = scannerId;
    },

    removeCouponsByIds(ids: number[]): void {
      const idSet = new Set(ids);
      this.coupons = this.coupons.filter((c) => !idSet.has(c.id));
      this.erroredCoupons = this.erroredCoupons.filter((c) => !idSet.has(c.id));
    },
  },
});
